/* Boss Trophy Wall — the five named boss worms (SKULLMAW, CINDERJAW, BONEHORN,
   IRONHIDE, DOOMGAZE) with this runner's kill counts. Guests read the local
   device tally; a linked Titan Tag pulls the cross-device ledger from
   GET /api/wasteland/trophies and merges by max. Refreshes live when a kill
   lands (wasteland-trophies-changed) or auth changes (dolo-auth). */
import { useEffect, useState } from "react";
import axios from "axios";
import { Skull, Trophy } from "lucide-react";
import { BOSS_WORMS } from "./wasteland/constants";
import { getLinkedTag } from "./PalsCloudSave";

const API = process.env.REACT_APP_BACKEND_URL;
const LOCAL_KEY = "wasteland-boss-trophies";

const readLocal = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(LOCAL_KEY) || "[0,0,0,0,0]");
    return Array.isArray(raw) && raw.length === 5 ? raw.map((n) => Math.max(0, Number(n) || 0)) : [0, 0, 0, 0, 0];
  } catch {
    return [0, 0, 0, 0, 0];
  }
};

export function WastelandTrophies() {
  const [kills, setKills] = useState(readLocal);

  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      const local = readLocal();
      const tag = getLinkedTag();
      if (!tag) {
        if (alive) setKills(local);
        return;
      }
      try {
        const res = await axios.get(`${API}/wasteland/trophies`, { params: { tag } });
        const server = Array.isArray(res.data?.kills) ? res.data.kills : [0, 0, 0, 0, 0];
        if (alive) setKills(local.map((n, i) => Math.max(n, Number(server[i]) || 0)));
      } catch {
        if (alive) setKills(local);
      }
    };
    refresh();
    window.addEventListener("wasteland-trophies-changed", refresh);
    window.addEventListener("dolo-auth", refresh);
    return () => {
      alive = false;
      window.removeEventListener("wasteland-trophies-changed", refresh);
      window.removeEventListener("dolo-auth", refresh);
    };
  }, []);

  const total = kills.reduce((sum, n) => sum + n, 0);

  return (
    <section className="trophy-wall" data-testid="boss-trophy-wall">
      <div className="trophy-wall-head">
        <span className="eyebrow">BOSS TROPHY WALL // NAMED WORM DUELS</span>
        <strong data-testid="trophy-wall-total">
          <Trophy size={13} /> {total > 0 ? `${total} BOSS ${total === 1 ? "WORM" : "WORMS"} FELLED` : "NO BOSSES FELLED YET"}
        </strong>
        <p>Every other delivery a named boss worm erupts — 10,000 hull, boss-level arsenal. Break one and its trophy turns on the wall.</p>
      </div>
      <div className="trophy-wall-grid">
        {BOSS_WORMS.map((boss, index) => {
          const felled = kills[index] > 0;
          return (
            <div key={boss.name} className={`trophy-card ${felled ? "is-felled" : ""}`} data-testid={`trophy-card-${index}`}>
              <div className="trophy-card-art">
                <img src={boss.src} alt={felled ? `${boss.name} boss worm trophy` : "Unbeaten boss worm silhouette"} loading="lazy" />
                {!felled ? (
                  <span className="trophy-card-lock">
                    <Skull size={12} /> UNBEATEN
                  </span>
                ) : null}
              </div>
              <strong>{boss.name}</strong>
              <em data-testid={`trophy-count-${index}`}>{felled ? `×${kills[index]} FELLED` : "HULL UNBROKEN"}</em>
            </div>
          );
        })}
      </div>
    </section>
  );
}
